import React, { useMemo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Button, Input, Divider } from '@attentive-platform/stem-ui';

import { AddDiscountIcon } from '../rosettaSvgs';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { calculateTradePriceForTradeHelper, calculatePricingHelper } from '../utils/rosettaPricingHelpers';

const PricingRow = ({ label, value, variant }) => {
  const className = variant ? `rosetta-pricing-row ${variant}` : 'rosetta-pricing-row';
  return (
    <div className={className}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};

const PricingBreakdownCard = () => {
  const {
    tradesData,
    selectedTrades,
    volumeDiscountTiers,
    manualDiscount,
    manualDiscountEnabled,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    projectsPerYear,
    sheetsPerProject,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      volumeDiscountTiers: state.volumeDiscountTiers,
      manualDiscount: state.manualDiscount,
      manualDiscountEnabled: state.manualDiscountEnabled,
      selectedScopes: state.selectedScopes,
      inflationPercentage: state.inflationPercentage,
      perTradeDistribution: state.perTradeDistribution,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject,
      dispatch: state.dispatch
    }))
  );

  const totalAnnualSheets = useMemo(() => projectsPerYear * sheetsPerProject, [projectsPerYear, sheetsPerProject]);

  const tradesMap = useMemo(() => {
    const map = new Map();
    tradesData.forEach(trade => map.set(trade.trade_id, trade));
    return map;
  }, [tradesData]);

  const pricing = useMemo(
    () =>
      calculatePricingHelper({
        selectedTrades,
        tradesData,
        selectedScopes,
        inflationPercentage,
        perTradeDistribution,
        totalAnnualSheets,
        volumeDiscountTiers
      }),
    [
      selectedTrades,
      tradesData,
      selectedScopes,
      inflationPercentage,
      perTradeDistribution,
      totalAnnualSheets,
      volumeDiscountTiers
    ]
  );

  const tradePrices = useMemo(() => {
    const prices = new Map();
    Array.from(selectedTrades).forEach(tradeId => {
      const trade = tradesMap.get(tradeId);
      const selectedScopeIds = Array.from(selectedScopes[tradeId] || []);
      prices.set(tradeId, calculateTradePriceForTradeHelper(trade, selectedScopeIds, inflationPercentage));
    });
    return prices;
  }, [selectedTrades, tradesMap, selectedScopes, inflationPercentage]);

  const manualDiscountPercent = useMemo(
    () => (manualDiscountEnabled ? manualDiscount / 100 : 0),
    [manualDiscountEnabled, manualDiscount]
  );

  const finalPrice = useMemo(
    () => pricing.priceAfterVolumeDiscount * (1 - manualDiscountPercent),
    [pricing.priceAfterVolumeDiscount, manualDiscountPercent]
  );

  const annualCost = useMemo(
    () => pricing.annualCostAfterVolumeDiscount * (1 - manualDiscountPercent),
    [pricing.annualCostAfterVolumeDiscount, manualDiscountPercent]
  );

  const handleManualDiscountToggle = useCallback(() => {
    dispatch({ type: 'SET_MANUAL_DISCOUNT_ENABLED', payload: !manualDiscountEnabled });
  }, [dispatch, manualDiscountEnabled]);

  const handleManualDiscountChange = useCallback(
    e => {
      const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
      dispatch({ type: 'SET_MANUAL_DISCOUNT', payload: value });
    },
    [dispatch]
  );

  return (
    <section className='rosetta-pricing-card'>
      <div className='rosetta-pricing-header'>
        <h2 className='rosetta-pricing-title'>Pricing Breakdown</h2>
        <Button variant='outlined' onClick={handleManualDiscountToggle} size='small'>
          <AddDiscountIcon />
        </Button>
      </div>

      {/* Price per Trade */}
      <div className='rosetta-price-per-trade-section'>
        <h3 className='rosetta-price-per-trade-title'>Price per Trade</h3>
        <div className='rosetta-trade-prices-list'>
          {Array.from(selectedTrades).map(tradeId => {
            const trade = tradesMap.get(tradeId);
            const tradePrice = tradePrices.get(tradeId) || 0;
            return (
              <div key={tradeId} className='rosetta-trade-price-item'>
                <span className='rosetta-trade-price-name'>{trade?.trade_name || ''}:</span>
                <span className='rosetta-trade-price-value'>${tradePrice.toFixed(1)}/sheet</span>
              </div>
            );
          })}
        </div>
      </div>

      <PricingRow label='Blended List Price:' value={`$${pricing.blendedPrice.toFixed(1)}/sheet`} />
      <PricingRow
        label='Volume Discount:'
        value={`-${(pricing.volumeDiscount * 100).toFixed(0)}%`}
        variant='discount'
      />
      {manualDiscountEnabled && (
        <PricingRow label='Manual Discount:' value={`-${manualDiscount}%`} variant='discount' />
      )}
      <PricingRow label='Final Price:' value={`$${finalPrice.toFixed(1)}/sheet`} variant='final' />
      <PricingRow
        label='Estimated Annual Cost:'
        value={`$${annualCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
        variant='total'
      />

      <Divider />

      {manualDiscountEnabled && (
        <div className='rosetta-form-group'>
          <label className='rosetta-form-label'>Manual Discount (%)</label>
          <Input
            min='0'
            max='100'
            step='0.5'
            size='small'
            type='number'
            placeholder='0'
            onChange={handleManualDiscountChange}
            value={manualDiscount === 0 ? '' : manualDiscount}
          />
        </div>
      )}
    </section>
  );
};

export default PricingBreakdownCard;
